import React, { useCallback, useEffect, useRef, useState } from 'react';
import EventAnalyticsPanel from './EventAnalyticsPanel';

export const time = (value) => (value ? new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) : '-');
export const status = (value) =>
    ({ created: '접수', queued: '대기', generating: '생성 중', completed: '완료', failed: '실패', printing: '출력 중', check_required: '출력 확인 필요', sent: '전송됨' })[value] || value || '-';

function SignedImage({ api, id, kind, label, available, thumb = false }) {
    const [revision, setRevision] = useState(0);
    const [image, setImage] = useState({ loading: true });
    useEffect(() => {
        const controller = new AbortController();
        let expiry;
        setImage({ loading: true });
        if (!available) {
            setImage({ error: '사진 없음' });
            return () => controller.abort();
        }
        api(`image?id=${encodeURIComponent(id)}&kind=${kind}`, undefined, 'GET', { signal: controller.signal })
            .then((result) => {
                const url = new URL(result.url);
                if (result.requestId !== id || result.expiresIn !== 300 || !['http:', 'https:'].includes(url.protocol)) throw Error('사진 응답을 확인하지 못했어요.');
                if (controller.signal.aborted) return;
                setImage({ url: url.href });
                expiry = setTimeout(() => setImage({ error: '사진 링크가 만료됐어요.' }), 300000);
            })
            .catch(() => {
                if (!controller.signal.aborted) setImage({ error: '사진을 불러오지 못했어요.' });
            });
        return () => {
            controller.abort();
            clearTimeout(expiry);
        };
    }, [api, id, kind, available, revision]);
    return (
        <figure className={'ai-image' + (thumb ? ' ai-thumb' : '')}>
            {image.url ? (
                <img src={image.url} alt={label} onError={() => setImage({ error: '사진을 불러오지 못했어요.' })} />
            ) : (
                <small role="status">{image.loading ? '사진 불러오는 중' : image.error}</small>
            )}
            {available && image.error && (
                <button type="button" onClick={() => setRevision((value) => value + 1)}>
                    사진 다시 불러오기
                </button>
            )}
            <figcaption>{label}</figcaption>
        </figure>
    );
}

export default function EventAdminPanel({ api }) {
    const [data, setData] = useState(null);
    const [devices, setDevices] = useState([]);
    const [view, setView] = useState('participants');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [pair, setPair] = useState(null);
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const active = useRef(false);
    const controller = useRef(null);
    const refreshing = useRef(null);
    const refresh = useCallback(() => {
        if (!active.current || refreshing.current) return refreshing.current;
        const run = Promise.all([api('state', undefined, 'GET', { signal: controller.current.signal }), api('print-bridge-devices', undefined, 'GET', { signal: controller.current.signal })])
            .then(([state, bridge]) => {
                if (!active.current) return;
                setData(state);
                setDevices(bridge.devices);
                setNotice('');
            })
            .catch((error) => {
                if (active.current) setNotice(error.response?.data?.error || '행사 기록을 불러오지 못했어요.');
            })
            .finally(() => {
                refreshing.current = null;
            });
        refreshing.current = run;
        return run;
    }, [api]);
    useEffect(() => {
        active.current = true;
        controller.current = new AbortController();
        void refresh();
        const interval = setInterval(() => {
            if (!document.hidden) void refresh();
        }, 15000);
        return () => {
            active.current = false;
            controller.current.abort();
            clearInterval(interval);
        };
    }, [refresh]);
    useEffect(() => {
        if (!pair) return undefined;
        const timer = setTimeout(() => setPair(null), 600000);
        return () => clearTimeout(timer);
    }, [pair]);
    useEffect(() => {
        const close = (event) => {
            if (event.key === 'Escape') {
                setSelected(null);
                setPreview(null);
            }
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, []);
    async function action(operation, payload, method = 'POST', message) {
        if (busy || (message && !window.confirm(message))) return;
        setBusy(true);
        try {
            const result = await api(operation, payload, method, { signal: controller.current.signal });
            if (!active.current) return;
            if (operation === 'print-bridge-pairing') setPair(result.code);
            await refresh();
            if (active.current) setNotice('요청을 처리했어요.');
        } catch (error) {
            if (active.current) setNotice(error.response?.data?.error || '요청을 처리하지 못했어요. 다시 실행하기 전에 현재 상태를 확인해 주세요.');
        } finally {
            if (active.current) setBusy(false);
        }
    }
    async function removeParticipant(row) {
        if (busy) return;
        setBusy(true);
        try {
            const value = await api(`participant-delete?id=${encodeURIComponent(row.id)}`, undefined, 'GET', { signal: controller.current.signal });
            if (
                !active.current ||
                !window.confirm(
                    `참여코드 ${row.participationCode} 기록과 연결된 출력 ${value.printCount}건을 목록에서 제외할까요?\n사진과 이용 기록은 보관돼요. 이미 프린터로 전송한 사진은 취소되지 않아요.`,
                )
            )
                return;
            await api('participant-delete', { id: row.id, version: value.version }, 'POST', { signal: controller.current.signal });
            if (!active.current) return;
            setSelected(null);
            setPreview(null);
            await refresh();
            setNotice('참여 기록을 목록에서 제외했어요.');
        } catch (error) {
            if (active.current) setNotice(error.response?.data?.error || '기록을 제외하지 못했어요.');
        } finally {
            if (active.current) setBusy(false);
        }
    }
    const participants = data?.participants || [];
    const prints = data?.prints || [];
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
    const durations = participants.map((row) => row.durationMs).filter(Boolean);
    const stats = [
        ['오늘 체험', participants.filter((row) => new Date(row.createdAt).toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }) === today).length],
        ['생성 완료', participants.filter((row) => row.status === 'completed').length],
        ['생성 대기·진행', participants.filter((row) => ['queued', 'generating'].includes(row.status)).length],
        ['생성 실패', participants.filter((row) => row.status === 'failed').length],
        ['평균 생성', durations.length ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length / 1000) + '초' : '-'],
        ['출력 대기', prints.filter((job) => job.status === 'queued').length],
    ];
    const rows = participants.filter((row) => !search || `${row.participationCode} ${row.id}`.toLowerCase().includes(search.toLowerCase().trim()));
    const detail = participants.find((row) => row.id === selected);
    const photo = (row, kind, label, thumb = false) => <SignedImage api={api} id={row.id} kind={kind} label={label} available={!!row[kind + 'Key']} thumb={thumb} />;
    const printButton = (row) => {
        const job = prints.find((item) => item.profileId === row.id);
        return (
            row.finalKey && (
                <button type="button" disabled={busy || !!job} onClick={() => action('print', { profileId: row.id, action: 'queue' })}>
                    {job ? '출력 접수됨 · ' + status(job.status) : '인쇄 요청'}
                </button>
            )
        );
    };
    return (
        <div>
            <div className="ai-summary">
                {stats.map(([label, value]) => (
                    <div className="ai-stat" key={label}>
                        <small>{label}</small>
                        <strong>{value}</strong>
                    </div>
                ))}
            </div>
            <nav aria-label="행사 관리 보기">
                {[
                    ['participants', '참여 기록'],
                    ['prints', '출력 관리'],
                    ['analytics', '이용 분석'],
                ].map(([key, label]) => (
                    <button
                        type="button"
                        key={key}
                        aria-pressed={view === key}
                        onClick={() => {
                            setView(key);
                            setSelected(null);
                            setPreview(null);
                        }}
                    >
                        {label}
                    </button>
                ))}
            </nav>
            <p className="ai-notice" role="status">
                {notice || (!data ? '행사 기록 불러오는 중' : '')}
            </p>
            {view === 'analytics' ? (
                <EventAnalyticsPanel api={api} />
            ) : (
                <>
                    <div className="ai-toolbar">
                        <label>
                            참여코드·요청 ID
                            <input value={search} onChange={(event) => setSearch(event.target.value)} />
                        </label>
                        <button type="button" disabled={busy} onClick={() => void refresh()}>
                            새로고침
                        </button>
                    </div>
                    {view === 'participants' && (
                        <div className="ai-table">
                            <table>
                                <thead>
                                    <tr>
                                        {['참여코드', '원본 사진', 'AI 결과', '상태', '정보', '접수 시각', '완료 시각', '작업'].map((label) => (
                                            <th key={label}>{label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                {row.participationCode}
                                                <small>{row.id}</small>
                                            </td>
                                            <td>
                                                {photo(row, 'original', '원본 사진', true)}
                                                <button type="button" onClick={() => setPreview({ row, kind: 'original', label: '원본 사진' })}>
                                                    원본 확대
                                                </button>
                                            </td>
                                            <td>
                                                {photo(row, row.finalKey ? 'final' : 'ai', 'AI 결과', true)}
                                                <button type="button" onClick={() => setPreview({ row, kind: row.finalKey ? 'final' : 'ai', label: 'AI 결과' })}>
                                                    결과 확대
                                                </button>
                                            </td>
                                            <td>
                                                {status(row.status)}
                                                <small>{row.error}</small>
                                                {row.durationMs ? <small>{Math.round(row.durationMs / 1000)}초</small> : null}
                                            </td>
                                            <td>
                                                {row.gender} · {row.birth}년생
                                                <small>
                                                    {row.hobby} · {row.style}
                                                </small>
                                            </td>
                                            <td>{time(row.createdAt)}</td>
                                            <td>{time(row.completedAt)}</td>
                                            <td>
                                                <div className="ai-actions">
                                                    {printButton(row)}
                                                    <button type="button" onClick={() => setSelected(row.id)}>
                                                        상세
                                                    </button>
                                                    <button type="button" className="ai-danger" disabled={busy} onClick={() => removeParticipant(row)}>
                                                        삭제
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!rows.length && <p>참여 기록이 없어요.</p>}
                        </div>
                    )}
                    {view === 'prints' && (
                        <>
                            <div className="ai-actions">
                                <button
                                    type="button"
                                    disabled={busy || !data}
                                    aria-pressed={!!data?.autoPrintEnabled}
                                    onClick={() =>
                                        action(
                                            'print-settings',
                                            { autoPrintEnabled: !data.autoPrintEnabled },
                                            'POST',
                                            data.autoPrintEnabled
                                                ? '자동 출력을 끌까요? 대기 사진은 유지되며 PC가 새로 가져가지 않아요.'
                                                : '자동 출력을 켤까요? 현재 대기 사진도 연결된 PC가 가져갈 수 있어요.',
                                        )
                                    }
                                >
                                    자동 출력 {data?.autoPrintEnabled ? 'ON' : 'OFF'}
                                </button>
                                <button type="button" disabled={busy} onClick={() => action('print-bridge-pairing', {})}>
                                    PC 연결 코드 만들기
                                </button>
                            </div>
                            {pair && (
                                <p role="status">
                                    연결 코드 <strong>{pair}</strong> · 10분 동안 한 번만 사용할 수 있어요. PC 설치 창에 입력해 주세요.
                                </p>
                            )}
                            <h3>등록된 PC</h3>
                            {devices.length ? (
                                <ul>
                                    {devices.map((device) => (
                                        <li key={device.id}>
                                            {device.name} · {time(device.createdAt)}{' '}
                                            <button
                                                type="button"
                                                className="ai-danger"
                                                disabled={busy}
                                                onClick={() => action('print-bridge-device', { id: device.id }, 'DELETE', '이 PC의 인쇄 연결을 해제할까요? 대기 사진을 더 이상 가져갈 수 없어요.')}
                                            >
                                                연결 해제
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>등록된 PC가 없어요.</p>
                            )}
                            <div className="ai-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>접수</th>
                                            <th>참여코드·요청</th>
                                            <th>상태</th>
                                            <th>작업</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {prints.map((job) => (
                                            <tr key={job.id}>
                                                <td>{time(job.createdAt)}</td>
                                                <td>
                                                    {participants.find((row) => row.id === job.profileId)?.participationCode}
                                                    <small>{job.profileId}</small>
                                                </td>
                                                <td>
                                                    {status(job.status)}
                                                    <small>{job.error}</small>
                                                </td>
                                                <td>
                                                    <button type="button" onClick={() => setSelected(job.profileId)}>
                                                        사진 보기
                                                    </button>
                                                    {job.status === 'queued' && (
                                                        <button
                                                            type="button"
                                                            disabled={busy}
                                                            className="ai-danger"
                                                            onClick={() => action('print', { jobId: job.id, action: 'remove' }, 'POST', '아직 전송하지 않은 이 대기 사진을 제외할까요?')}
                                                        >
                                                            대기열 제외
                                                        </button>
                                                    )}
                                                    {job.status === 'check_required' && (
                                                        <button
                                                            type="button"
                                                            disabled={busy}
                                                            onClick={() =>
                                                                action(
                                                                    'print',
                                                                    { profileId: job.profileId, action: 'complete' },
                                                                    'POST',
                                                                    '실제로 출력된 사진을 확인했나요? 출력 확인 완료로 기록합니다.',
                                                                )
                                                            }
                                                        >
                                                            출력 확인
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {!prints.length && <p>출력 요청이 없어요.</p>}
                            </div>
                        </>
                    )}
                </>
            )}
            {(detail || preview) && (
                <div
                    className="ai-modal"
                    role="presentation"
                    onClick={() => {
                        setSelected(null);
                        setPreview(null);
                    }}
                >
                    <section className="ai-dialog" role="dialog" aria-modal="true" aria-label={preview ? preview.label : '참여 기록 상세'} onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => {
                                setSelected(null);
                                setPreview(null);
                            }}
                        >
                            닫기
                        </button>
                        {preview ? (
                            photo(preview.row, preview.kind, preview.label)
                        ) : (
                            <>
                                <h2>
                                    참여코드 {detail.participationCode} · {status(detail.status)}
                                </h2>
                                <p>{detail.id}</p>
                                <div className="ai-photos">
                                    {[
                                        ['original', '원본'],
                                        ['ai', 'AI 결과'],
                                        ['final', '최종 출력물'],
                                    ].map(([kind, label]) => (
                                        <React.Fragment key={kind}>{photo(detail, kind, label)}</React.Fragment>
                                    ))}
                                </div>
                                <dl className="ai-details">
                                    {[
                                        ['성별', detail.gender],
                                        ['출생 연도', detail.birth],
                                        ['취미', detail.hobby],
                                        ['종류', detail.style],
                                        ['접수', time(detail.createdAt)],
                                        ['완료', time(detail.completedAt)],
                                        ['실패', time(detail.failedAt)],
                                        ['생성 소요', detail.durationMs ? Math.round(detail.durationMs / 1000) + '초' : '-'],
                                    ].map(([label, value]) => (
                                        <React.Fragment key={label}>
                                            <dt>{label}</dt>
                                            <dd>{value}</dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                                {detail.error && (
                                    <p className="ai-notice" role="alert">
                                        생성 오류: {detail.error}
                                    </p>
                                )}
                                {printButton(detail)}
                            </>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
